import { useApi } from '@core/api'
import ErrorHandler from '@core/api/ErrorHandler'
import {
  type GetJobDetailsOutput,
  type GetJobsOutput,
  type JobDetailsOutput,
  type JobItemOutput,
  type JobOutput,
} from '@core/api/generated'
import observabilityProvider from '@core/api/observability'
import TIMEOUTS from '@core/constants/timeouts'
import { addSnack } from '@core/snack/snack-state'
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query'

import { useBatches } from './batches-state'

const captureBatchError = (scope: string, error: unknown) => {
  const err =
    error instanceof Error ? error : new Error(typeof error === 'string' ? error : 'Unknown error')
  console.error(`batches-api ${scope} failed`, error)
  observabilityProvider.captureException(err)
}

export type Batch = JobOutput
export type BatchDetails = JobDetailsOutput
export type BatchItem = JobItemOutput

const QUERY_KEY_ROOT = ['author-batches'] as const

const queryKeys = {
  all: QUERY_KEY_ROOT,
  list: [...QUERY_KEY_ROOT, 'list'] as const,
  details: (jobId: string) => [...QUERY_KEY_ROOT, 'details', jobId] as const,
}

const TERMINAL_ITEM_STATUSES = new Set(['DONE', 'FAILED'])

// A batch is "settled" once every required item has terminated (passed or
// failed). All-failed batches are settled — they will never produce a DONE
// item and must not keep us in fast-poll forever.
const isStillGenerating = (b: Batch) => b.itemsFailed + b.itemsPassed < b.itemsRequired

export const getJobListRefetchInterval = (
  batches: readonly Batch[],
  selectedBatchId: string | null,
) => {
  // No batches → idle. Nothing the user is waiting on.
  if (batches.length === 0) return TIMEOUTS.BATCH_LIST_POLL_IDLE_MS

  // Selected batch is fresh and has no DONE item yet → user is staring at the
  // loader post-generate; BatchesDataLayer auto-navigates the moment we see
  // one. Poll fast so the redirect feels instant.
  const selectedAwaitingFirstDone =
    !!selectedBatchId &&
    batches.some((b) => b.id === selectedBatchId && !b.firstItemDoneId && isStillGenerating(b))
  if (selectedAwaitingFirstDone) return TIMEOUTS.BATCH_LIST_POLL_AWAITING_FIRST_DONE_MS

  // Some other batch is still generating in the background → moderate poll so
  // its row in the autocomplete fills in without waiting a full minute.
  if (batches.some(isStillGenerating)) return TIMEOUTS.BATCH_LIST_POLL_ACTIVE_MS

  // Steady state: every batch has settled.
  return TIMEOUTS.BATCH_LIST_POLL_IDLE_MS
}

export const getJobDetailsRefetchInterval = (items: readonly BatchItem[]) => {
  // No items yet → fresh batch, server hasn't materialized rows. Poll fast so
  // the empty Batches tab fills in instead of sitting blank for 30s.
  if (items.length === 0) return TIMEOUTS.BATCH_DETAILS_POLL_AWAITING_FIRST_DONE_MS

  // All items are terminal (DONE or FAILED) → nothing more to fetch. Must come
  // before the "no DONE yet" branch so all-failed batches don't poll forever.
  const hasInFlight = items.some((i) => !TERMINAL_ITEM_STATUSES.has(i.status))
  if (!hasInFlight) return TIMEOUTS.BATCH_DETAILS_POLL_IDLE_MS

  // Items in flight and none has finished yet → user is looking at all-skeleton
  // rows. Poll fast so the first row populates ASAP.
  const hasAnyDone = items.some((i) => i.status === 'DONE')
  if (!hasAnyDone) return TIMEOUTS.BATCH_DETAILS_POLL_AWAITING_FIRST_DONE_MS

  // At least one item is DONE; others still in flight → moderate poll.
  return TIMEOUTS.BATCH_DETAILS_POLL_ACTIVE_MS
}

export const useJobList = () => {
  const api = useApi()
  return useQuery<GetJobsOutput>({
    queryKey: queryKeys.list,
    queryFn: async () => {
      const { data, error } = await api.getJobs({ throwOnError: false })
      if (error || !data) {
        const thrown = error ?? new Error('Failed to load batches')
        captureBatchError('useJobList', thrown)
        throw thrown
      }
      return data
    },
    retry: false,
    refetchInterval: ({ state }) =>
      getJobListRefetchInterval(state.data?.batches ?? [], useBatches.getState().selectedBatchId),
  })
}

const buildJobDetailsQuery = (api: ReturnType<typeof useApi>, jobId: string) => ({
  queryKey: queryKeys.details(jobId),
  queryFn: async (): Promise<GetJobDetailsOutput> => {
    const { data, error } = await api.getJobDetails({
      path: { job_id: jobId },
      throwOnError: false,
    })
    if (error || !data) {
      const thrown = error ?? new Error('Failed to load batch details')
      captureBatchError('useJobDetails', thrown)
      throw thrown
    }
    return data
  },
  retry: false,
  refetchInterval: ({ state }: { state: { data?: GetJobDetailsOutput } }) =>
    getJobDetailsRefetchInterval(state.data?.batch?.items ?? []),
})

export const useJobDetails = <TData = GetJobDetailsOutput>(
  jobId: string | null,
  options?: { select?: (data: GetJobDetailsOutput) => TData; enabled?: boolean },
) => {
  const api = useApi()
  return useQuery<GetJobDetailsOutput, unknown, TData>({
    ...buildJobDetailsQuery(api, jobId ?? ''),
    enabled: !!jobId && options?.enabled !== false,
    select: options?.select,
  })
}

export const useJobDetailsSuspense = <TData = GetJobDetailsOutput>(
  jobId: string,
  options?: { select?: (data: GetJobDetailsOutput) => TData },
) => {
  const api = useApi()
  return useSuspenseQuery<GetJobDetailsOutput, unknown, TData>({
    ...buildJobDetailsQuery(api, jobId),
    select: options?.select,
  })
}

export const useDiscardJobs = () => {
  const api = useApi()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (ids: string[]): Promise<boolean> => {
      try {
        const { data, error } = await api.discardJobs({
          body: { ids },
          throwOnError: false,
        })
        if (error || !data) {
          throw error ?? new Error('Failed to discard batches')
        }

        const success = data.affectedRows > 0
        if (success) {
          for (const id of ids) {
            queryClient.removeQueries({ queryKey: queryKeys.details(id) })
          }
          await queryClient.invalidateQueries({ queryKey: queryKeys.list })

          useBatches.setState((prev) => {
            prev.selectedDiscard = []
            if (prev.selectedBatchId && ids.includes(prev.selectedBatchId)) {
              prev.selectedBatchId = null
            }
          })
        }

        return success
      } catch (error) {
        captureBatchError('useDiscardJobs', error)
        const { message } = ErrorHandler(error) as { message: string }
        addSnack({ severity: 'error', message })
        return false
      }
    },
  })
}

export const useUpsertOptimisticBatch = () => {
  const queryClient = useQueryClient()

  return (batch: Batch) => {
    queryClient.setQueryData<GetJobsOutput>(queryKeys.list, (prev) => {
      const existing = prev?.batches ?? []
      if (existing.some((b) => b.id === batch.id)) {
        return prev
      }
      return { batches: [batch, ...existing] }
    })
    useBatches.setState({ selectedBatchId: batch.id })
    return queryClient.invalidateQueries({ queryKey: queryKeys.all })
  }
}
